import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, ArrowUp, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import BottomSheetPanel from '@/components/BottomSheetPanel';
import ClickableUserDisplay from '@/components/ClickableUserDisplay';
import { useComments, Comment } from '@/hooks/useComments';
import { useAuth } from '@/hooks/useAuth';
import { useTranslation } from '@/hooks/useTranslation';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface CommentsPanelProps {
  mediaId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onCountChange?: (delta: number) => void;
}

export const CommentsPanel: React.FC<CommentsPanelProps> = ({ mediaId, isOpen, onClose, onCountChange }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { comments, addComment, editComment, deleteComment, refetch } = useComments(mediaId);
  const [newComment, setNewComment] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const requestCloseRef = useRef<() => void>(() => {});
  const touchGestureRef = useRef<{ touchStartY: number; atTopOnStart: boolean }>({
    touchStartY: 0,
    atTopOnStart: false,
  });

  const getViewportEl = () => {
    const root = scrollRef.current;
    if (!root) return null;
    const vp = root.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement | null;
    return vp || (root as unknown as HTMLElement);
  };

  const closeWithSlide = () => {
    requestCloseRef.current();
  };

  useEffect(() => {
    const vp = getViewportEl();
    if (vp && comments.length > 0) {
      vp.scrollTop = vp.scrollHeight;
    }
  }, [comments.length]);

  useEffect(() => {
    if (!isOpen || !mediaId) return;

    refetch();

    const interval = setInterval(() => {
      refetch();
    }, 4000);

    return () => clearInterval(interval);
  }, [isOpen, mediaId, refetch]);

  const handleAddComment = async () => {
    if (!newComment.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await addComment(newComment);
      setNewComment('');
      onCountChange?.(1);
      requestAnimationFrame(() => {
        const vp = getViewportEl();
        if (vp) vp.scrollTo({ top: vp.scrollHeight, behavior: 'smooth' });
      });
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditComment = async (commentId: string) => {
    if (!editContent.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await editComment(commentId, editContent);
      setEditingId(null);
      setEditContent('');
    } catch (error) {
      console.error('Error editing comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      await deleteComment(commentId);
      onCountChange?.(-1);
    } catch (error) {
      console.error('Error deleting comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const startEditing = (comment: Comment) => {
    setEditingId(comment.id);
    setEditContent(comment.content);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditContent('');
  };

  useEffect(() => {
    if (!isOpen) return;
    const vp = getViewportEl();
    if (!vp) return;

    const onWheel = (e: WheelEvent) => {
      if (e.deltaY < 0 && vp.scrollTop <= 0) {
        e.preventDefault();
        e.stopPropagation();
        closeWithSlide();
      }
    };

    let touchStartY: number | null = null;
    const onTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0]?.clientY ?? null;
    };
    const onTouchMove = (e: TouchEvent) => {
      if (touchStartY == null) return;
      const dy = (e.touches[0]?.clientY ?? 0) - touchStartY;
      if (dy > 40 && vp.scrollTop <= 0) {
        e.preventDefault();
        e.stopPropagation();
        touchStartY = null;
        closeWithSlide();
      }
    };

    vp.addEventListener('wheel', onWheel, { passive: false });
    vp.addEventListener('touchstart', onTouchStart, { passive: true });
    vp.addEventListener('touchmove', onTouchMove, { passive: false });

    return () => {
      vp.removeEventListener('wheel', onWheel);
      vp.removeEventListener('touchstart', onTouchStart);
      vp.removeEventListener('touchmove', onTouchMove);
    };
  }, [isOpen]);

  return (
    <BottomSheetPanel
      isOpen={isOpen}
      onClose={onClose}
      title={t('comments')}
      count={comments.length}
    >
      {(requestClose) => {
        requestCloseRef.current = requestClose;
        return (
          <>
            <ScrollArea
              className="flex-1 min-h-0 px-6"
              ref={scrollRef}
              onWheelCapture={(e: React.WheelEvent<HTMLDivElement>) => {
                const vp = getViewportEl();
                if (!vp) return;
                if (e.deltaY < 0 && vp.scrollTop <= 0) {
                  e.preventDefault();
                  e.stopPropagation();
                  closeWithSlide();
                }
              }}
              onTouchStart={(e: React.TouchEvent<HTMLDivElement>) => {
                touchGestureRef.current.touchStartY = e.touches?.[0]?.clientY ?? 0;
                touchGestureRef.current.atTopOnStart = (getViewportEl()?.scrollTop ?? 0) <= 0;
              }}
              onTouchMove={(e: React.TouchEvent<HTMLDivElement>) => {
                const startY = touchGestureRef.current.touchStartY;
                const atTopOnStart = touchGestureRef.current.atTopOnStart;
                const currentY = e.touches?.[0]?.clientY ?? 0;
                const dy = currentY - startY;
                if (atTopOnStart && dy > 35) {
                  e.preventDefault();
                  e.stopPropagation();
                  closeWithSlide();
                }
              }}
            >
              {comments.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <MessageCircle className="w-12 h-12 text-muted-foreground/50 mb-2" />
                  <p className="text-muted-foreground">{t('noComments')}</p>
                  <p className="text-sm text-muted-foreground/70">{t('beFirstToComment')}</p>
                </div>
              ) : (
                <div className="space-y-4 pb-4">
                  {comments.map((comment) => (
                    <div key={comment.id} className="flex gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 min-w-0">
                          <ClickableUserDisplay
                            userId={comment.user_id}
                            displayName={comment.display_name || 'Usuario anónimo'}
                            avatarUrl={comment.avatar_url}
                            variant="compact"
                            className="min-w-0"
                          />
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(comment.created_at), 'PPp', { locale: es })}
                          </span>
                        </div>

                        {editingId === comment.id ? (
                          <div className="space-y-2">
                            <Textarea
                              value={editContent}
                              onChange={(e) => setEditContent(e.target.value)}
                              className="w-full min-h-[60px] text-base md:text-sm focus-visible:border-ring focus-visible:ring-inset focus-visible:ring-offset-0"
                              autoFocus
                            />
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleEditComment(comment.id)}
                                disabled={!editContent.trim() || isSubmitting}
                              >
                                <Check className="w-3 h-3 mr-1" />
                                {t('save')}
                              </Button>
                              <Button size="sm" variant="ghost" onClick={cancelEditing}>
                                {t('cancel')}
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <p className="text-sm whitespace-pre-wrap break-words">{comment.content}</p>

                            {user?.id === comment.user_id && (
                              <div className="flex gap-2 mt-2">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => startEditing(comment)}
                                  disabled={isSubmitting}
                                  className="h-7 p-0 mr-2 text-[10px] text-foreground/70 hover:bg-transparent hover:text-foreground"
                                >
                                  {t('edit')}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDeleteComment(comment.id)}
                                  disabled={isSubmitting}
                                  className="h-7 p-0 text-[10px] text-foreground/70 hover:text-foreground hover:bg-transparent"
                                >
                                  {t('delete')}
                                </Button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>

            <div className="border-t border-foreground/20 p-4 shrink-0 sticky bottom-0 left-0 right-0 bg-background" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 25px)', zIndex: 1 }}>
              <div className="relative w-full">
                <Input
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder={t('addComment')}
                  className="w-full h-14 text-base md:text-sm pr-14 rounded-full"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddComment();
                    }
                  }}
                />
                {newComment.trim().length > 0 && (
                  <button
                    type="button"
                    aria-label="Enviar"
                    onClick={handleAddComment}
                    disabled={isSubmitting}
                    className="absolute bottom-2 right-2 h-10 w-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-md disabled:opacity-50"
                  >
                    <ArrowUp className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          </>
        );
      }}
    </BottomSheetPanel>
  );
};
